import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

/**
 * Per-route SEO updater. Calls /api/public/seo/meta for the current pathname
 * and updates <title>, meta description, canonical, og:title, og:description, og:image.
 *
 * SPA SEO has limits — Google now renders JS, Bing partially. For best results
 * the backend sitemap.xml is the canonical source of truth for crawlers.
 */
function setOrCreate(tag, attr, attrValue, content) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`${tag}[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  if (tag === "link") el.setAttribute("href", content);
  else el.setAttribute("content", content);
}

export default function SEOHead() {
  const location = useLocation();
  useEffect(() => {
    let cancelled = false;

    // URL-ul REAL al paginii curente (origin + calea din SPA). Sursa de adevăr
    // pentru canonical/og:url în browser — NICIODATĂ homepage-ul. Îl setăm imediat,
    // înainte (și independent) de răspunsul backend-ului, ca să fie corect chiar
    // dacă fetch-ul de meta eșuează.
    const origin = window.location.origin;
    const pageUrl = origin + location.pathname;
    setOrCreate("link", "rel", "canonical", pageUrl);
    setOrCreate("meta", "property", "og:url", pageUrl);
    setOrCreate("meta", "property", "og:type", "website");

    (async () => {
      try {
        const { data } = await api.get("/public/seo/meta", { params: { path: location.pathname } });
        if (cancelled) return;
        document.title = data.title;
        setOrCreate("meta", "name", "description", data.description);
        // Canonical/og:url rămân pe URL-ul real al paginii curente. Folosim
        // data.canonical de la backend DOAR dacă e o cale ce corespunde paginii
        // curente (evită „lipirea" pe homepage dintr-o config greșită).
        if (typeof data.canonical === "string" && data.canonical === location.pathname) {
          setOrCreate("link", "rel", "canonical", origin + data.canonical);
          setOrCreate("meta", "property", "og:url", origin + data.canonical);
        }
        // Open Graph
        setOrCreate("meta", "property", "og:title", data.title);
        setOrCreate("meta", "property", "og:description", data.description);
        if (data.og_image) setOrCreate("meta", "property", "og:image", data.og_image);
        // Twitter
        setOrCreate("meta", "name", "twitter:card", data.og_image ? "summary_large_image" : "summary");
        setOrCreate("meta", "name", "twitter:title", data.title);
        setOrCreate("meta", "name", "twitter:description", data.description);
        if (data.og_image) setOrCreate("meta", "name", "twitter:image", data.og_image);
      } catch {
        // Silent — canonical/og:url sunt deja setate pe URL-ul real al paginii.
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);
  return null;
}
